/*
 * (C) Behaviour Interactive Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium, is strictly prohibited
 * This file is proprietary and confidential
 */

import classNames from 'classnames';
import { AnimatePresence, AnimationProps, motion } from 'framer-motion';
import { ReactNode, SVGProps, useEffect, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../Elements/Button';

import CloseIcon from '@icons/Close.svg';
import InfoOutlineIcon from '@icons/InfoOutline.svg';
import SuccessOutlineIcon from '@icons/SuccessOutline.svg';
import WarningOutlineIcon from '@icons/WarningOutline.svg';

export interface Props {
  /**
   * ClassName to be applied to the toast
   */
  className?: string;
  /**
   * Whether the toast is displayed
   */
  display?: boolean;
  /**
   * Time that the toast should be kept visible (in ms)
   */
  timeout?: number;
  /**
   * Title of the toast
   */
  title?: string;
  /**
   * Description text of the toast
   */
  description?: string;
  /**
   * Theme of the toast
   */
  theme?: 'default' | 'info' | 'error' | 'warning' | 'success';
  /**
   * Custom icon component to display
   */
  icon?: FC<SVGProps<SVGElement>>;
  /**
   * Callback to be called on the close button click
   */
  onClose?: () => void;
}

const Icon = ({ className = '', theme, icon: CustomIcon }: Props): ReactNode => {
  if (CustomIcon) {
    return <CustomIcon className={className} />;
  }
  switch (theme) {
    case 'info':
      return <InfoOutlineIcon className={className} />;
    case 'error':
    case 'warning':
      return <WarningOutlineIcon className={className} />;
    case 'success':
      return <SuccessOutlineIcon className={className} />;
    default:
      return;
  }
};

export const Toast = ({
  className = '',
  display = true,
  timeout,
  title,
  description,
  theme = 'default',
  icon,
  onClose,
}: Props) => {
  const { t } = useTranslation('common', { keyPrefix: 'common' });
  const [toastDisplay, setToastDisplay] = useState(display);

  const closeCallback = () => {
    if (onClose) {
      onClose();
    }
    setToastDisplay(false);
  };

  useEffect(() => {
    setToastDisplay(display);
    if (display && timeout) {
      const toastTimeout = setTimeout(() => {
        closeCallback();
      }, timeout);

      return () => {
        clearTimeout(toastTimeout);
      };
    }
  }, [display, timeout]);

  const toastMotionProps: AnimationProps = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  };

  const toastClasses = classNames(
    'flex items-center justify-between gap-3',
    'w-full',
    'bg-surface-medium',
    'px-4 py-1',
    'font-sans text-md text-neutral-text-light',
    'rounded-md border',
    {
      'border-transparent': theme === 'default',
      'border-neutral-text-xlight': theme === 'info',
      'border-feedback-error': theme === 'error',
      'border-feedback-warning': theme === 'warning',
      'border-feedback-success': theme === 'success',
    },
    className,
  );

  return (
    <div
      aria-hidden={!toastDisplay}
      role='alertdialog'
      aria-live='polite'
      aria-atomic='true'
      className={classNames('flex items-end justify-center', {
        'invisible transition-all duration-500': !toastDisplay,
      })}
    >
      <AnimatePresence>
        {toastDisplay && (
          <motion.div {...toastMotionProps} className={toastClasses} tabIndex={-1}>
            <Icon
              className={classNames('h-6 w-6', {
                'text-secondary-softlight': theme === 'default',
                'text-neutral-text-xlight': theme === 'info',
                'text-feedback-error': theme === 'error',
                'text-feedback-warning': theme === 'warning',
                'text-feedback-success': theme === 'success',
              })}
              theme={theme}
              icon={icon}
            />
            <div className={classNames('flex flex-col justify-start', 'grow')}>
              {title && <h3 className='font-semibold'>{title}</h3>}
              {description && <p>{description}</p>}
            </div>
            <Button title={t('close')} variant='plain' size='medium' icon={CloseIcon} onClick={closeCallback} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
